import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { Button, Label, Switch, Input, AlertBar, Spinner } from 'scorer-ui-kit';
import { useTranslation } from 'react-i18next';
import Popup from 'reactjs-popup';
import {
  RightAlignedContainer,
  CenterAlignedContainer,
  LeftButton,
  overlayStyle,
  AlertContainer,
  SpinnerDesign,
} from '../Style';
import { getSnmpDetails, putSnmpDetails } from '../apis';
import styled from 'styled-components';
import '../css/popup.css';
import { regExIP, regExNumber } from '../constants';

const SnmpContainer = styled.div`
  width: 472px;
`; 

const PopupContainer = styled.div`
  padding: 30px;
  width: 600px;
`;

const RightContainer = styled(RightAlignedContainer)`
  margin-bottom: 30px;
`;

const SwitchContainer = styled.div`
  height: 25px;
  margin-bottom: 5px;
  position: relative;
`;

const RightPositionedSwitch = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;

interface SnmpInput {
  manager_host_ip1: string;
  manager_host_ip2: string;
  manager_host_ip3: string;
}

const SnmpSettings: React.FC = () => {
  const [snmpInput, setSnmpInput] = useState<SnmpInput>({
    manager_host_ip1: '',
    manager_host_ip2: '',
    manager_host_ip3: '',
  });
  const [snmpWarning, setSnmpWarning] = useState<string>('');
  const [alert, setAlert] = useState<{ key: any, message: string, type: 'success' | 'error' | 'warning' | 'info' | undefined }>({ key: new Date(), message: '', type: undefined });
  const [loading, setLoading] = useState<boolean>(true);
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [isSwitchEnabled, setIsSwitchEnabled] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { t } = useTranslation(['SnmpSettings', 'Common']);

  const getALLSnmpDetails = useCallback(()=>{
    getSnmpDetails()
      .then((res) => {
        if (res.data.status_code === '0') {
          const {manager_host_ip1, manager_host_ip2, manager_host_ip3, enabled} = res.data.data;
          setIsSwitchEnabled(enabled);
          setSnmpInput({manager_host_ip1, manager_host_ip2, manager_host_ip3});
          setReadOnly(true);
          setLoading(false);
        } else {
          setAlert({ key: new Date(), message: res.data.message, type: 'error' });
        }
      })
      .catch((err) => {
        if (err.response.data.message) {
          setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
        } else {
          console.error(err);
        }
      });
  },[]);

  useEffect(()=>{
    getALLSnmpDetails();
  },[getALLSnmpDetails]);

  const onSnmpInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setSnmpInput((prevVal) => {
      if(regExNumber.exec(value.replaceAll(".",''))){
        return { ...prevVal, [name]: value };
      } else {
        return prevVal;
      }
    });
  };

  function hasDuplicates(array: string[]) {
    array = array.filter(item => item!=='');
    return (new Set(array)).size !== array.length;
  };

  const validateIP = useCallback((inputIp: string) => {
    if (regExIP.exec(inputIp)) {
      return true;
    } else {
      return false;
    }
  }, []);

  const onSnmpSave = useCallback(() => {
    let snmpArr = Object.values(snmpInput);
    let isValidIpsArr: number[] | undefined = [];
    // eslint-disable-next-line
    if (isSwitchEnabled && snmpArr.every((item) => item == '')) {
      setSnmpWarning(t('pleaseEnterAtleastOneIPWarning'));
      setModalOpen(true);
    } else if(hasDuplicates(snmpArr)){
      setSnmpWarning(t('managerHostIpMustBeUnique'));
      setModalOpen(true);
    } else {
      snmpArr.forEach((item, index) => {
        if (item !== '') {
          if (!validateIP(item)) {
            isValidIpsArr?.push(index + 1);
          }
        }
      });
      if (isValidIpsArr.length !== 0) {
          let str = '';
          isValidIpsArr.forEach((item, index) => {
            if (index + 1 !== isValidIpsArr?.length) {
              str = str + item + ' & ';
            } else {
              str = str + item;
            }
          });
          setSnmpWarning(`${t('invalidIpWarning')} ${str}`);
          setModalOpen(true);
      } else {
        putSnmpDetails({...snmpInput, enabled: isSwitchEnabled})
          .then((res) => {  
            if (res.data.status_code === '0') {
              setLoading(true);
              getALLSnmpDetails();
              setTimeout(() => setAlert({ key: new Date(), message: t('successfullyUpdated'), type: 'success' }), 500);
            } else {
              setAlert({ key: new Date(), message: res.data.message, type: 'error' });
            }
          })
          .catch((err) => {
            if (err.response.data.message) {
              setAlert({ key: new Date(), message: err.response.data.message, type: 'error' });
            } else {
              console.error(err);
            }
          });
      }
    }
  }, [t, snmpInput, isSwitchEnabled, validateIP, getALLSnmpDetails]);

  const closeModal = () => {
    setModalOpen(false);
  };

  const onEdit = () => {
    setAlert({ key: new Date(), message: '', type: undefined });
    setReadOnly(false);
  };

  const onCancel = () => {
    setAlert({ key: new Date(), message: '', type: undefined });
    setLoading(true);
    getALLSnmpDetails();
    setReadOnly(true);
  };

  return (
    <>
      <Popup
        modal
        open={modalOpen}
        onClose={closeModal}
        {...{ overlayStyle }}
        closeOnDocumentClick
      >
        <PopupContainer style={{ width: '400px' }}>
          <CenterAlignedContainer>
            <p>{snmpWarning}</p>
          </CenterAlignedContainer>
          <RightAlignedContainer>
            <Button
              design='primary'
              size='small'
              onClick={() => {
                setSnmpWarning('');
                closeModal();
              }}
            >
              {t('Common:Ok')}
            </Button>
          </RightAlignedContainer>
        </PopupContainer>
      </Popup>
      {loading ?
        <CenterAlignedContainer>
          <Spinner size='large' styling={SpinnerDesign} />
        </CenterAlignedContainer> :
        <SnmpContainer>
          <SwitchContainer>
            <RightPositionedSwitch>
              <Switch
                checked={isSwitchEnabled}
                labelText={t(`${ isSwitchEnabled ? 'enabled' : 'disabled' }`)}
                leftTheme='off'
                rightTheme='on'
                state={readOnly? 'disabled' : 'default'}
                onChangeCallback={(e) => {            
                  setIsSwitchEnabled((prevVal) => readOnly? prevVal : e);
                }}
              />
            </RightPositionedSwitch>
          </SwitchContainer>
          <Label htmlFor='manager_host_ip1' labelText={t('managerHostIp1')}>
            <Input
              name='manager_host_ip1'
              placeholder={t('example') + ' - 192.168.30.16'}
              value={snmpInput.manager_host_ip1}
              onChange={onSnmpInputChange}
              disabled={readOnly}
            />
          </Label>
          <Label htmlFor='manager_host_ip2' labelText={t('managerHostIp2')}>
            <Input
              name='manager_host_ip2'
              placeholder={t('example') + ' - 192.168.30.16'}
              value={snmpInput.manager_host_ip2}
              onChange={onSnmpInputChange}
              disabled={readOnly}
            />
          </Label>
          <Label htmlFor='manager_host_ip3' labelText={t('managerHostIp3')}>
            <Input
              name='manager_host_ip3'
              placeholder={t('example') + ' - 192.168.30.16'}
              value={snmpInput.manager_host_ip3}
              onChange={onSnmpInputChange}
              disabled={readOnly}
            />
          </Label>
          <RightContainer>
            {readOnly ? (
              <Button design='primary' onClick={onEdit}>
                {t('Common:edit')}
              </Button>
            ) : (
              <>
                <LeftButton design='secondary' onClick={onCancel}>
                  {t('Common:cancel')}
                </LeftButton>
                <Button design='primary' onClick={onSnmpSave}>
                  {t('Common:save')}
                </Button>
              </>
            )}
          </RightContainer>
          {
            (alert.message !== '' && !modalOpen &&
              <AlertContainer key={alert.key}>
                <AlertBar type={alert.type} message={alert.message} />
              </AlertContainer>
            )
          }
        </SnmpContainer>}
    </>
  );
}

export default SnmpSettings;